import React, { useState } from "react";
import { toast } from "sonner";
import { ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { ANSWER_LABELS, ANSWER_STYLES } from "@/lib/vendors";
import { EvidenceUploadButton } from "@/components/rights/EvidenceUploadButton";

export const QuestionRow = ({ a, q, editable, onSaved, onChanged }) => {
  const r = q.response || {};
  const [comment, setComment] = useState(r.comment || "");
  const gap = ["NO", "PARTIAL"].includes(r.answer);
  const save = async (body) => {
    try { onSaved((await api.put(`/vendors/${a.vendor.id}/assessments/${a.id}/responses`, { answers: [{ question_key: q.key, ...body }] })).data); }
    catch (e) { toast.error(apiError(e, "Could not save answer")); }
  };
  const raise = async () => {
    try { const { data } = await api.post(`/vendors/${a.vendor.id}/assessments/${a.id}/responses/${q.key}/task`, {}); toast.success(`${data.task_ref} raised`); onChanged(); }
    catch (e) { toast.error(apiError(e, "Could not raise task")); }
  };
  return (
    <div className="py-3 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-2" data-testid={`question-${q.key}`}>
      <div className="min-w-0">
        <div className="text-sm text-[#003366]"><span className="font-mono text-[10px] text-accent-sm mr-1.5">{q.key}</span>{q.text}</div>
        {(editable || comment) && (
          <Input value={comment} disabled={!editable} onChange={(e) => setComment(e.target.value)} placeholder={gap ? "Comment required for gaps" : "Comment"}
                 onBlur={() => comment !== (r.comment || "") && save({ comment })}
                 className={`h-8 text-xs mt-2 ${gap && !comment.trim() ? "border-amber-300" : ""}`} data-testid={`question-comment-${q.key}`} />
        )}
        {r.carried_forward && <div className="text-[10px] text-slate-400 mt-1">Carried forward from the previous assessment</div>}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap lg:justify-end">
        {Object.keys(ANSWER_LABELS).map((k) => (
          <button key={k} disabled={!editable} onClick={() => save({ answer: k })} data-testid={`answer-${q.key}-${k.toLowerCase()}`}
                  className={`h-8 px-3 rounded-md border text-[11px] font-semibold transition-colors disabled:cursor-not-allowed ${
                    r.answer === k ? ANSWER_STYLES[k] : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {ANSWER_LABELS[k]}
          </button>
        ))}
        {editable && r.answer && <EvidenceUploadButton endpoint={`/vendors/${a.vendor.id}/evidence`} category="ASSESSMENT"
                                                       fields={{ review_id: a.id, question_key: q.key }} onUploaded={onChanged}
                                                       label={`${r.evidence_file_ids?.length || 0}`} testId={`question-attach-${q.key}`} />}
        {!editable && r.evidence_file_ids?.length > 0 && <span className="text-[10px] font-mono text-slate-400">{r.evidence_file_ids.length} file(s)</span>}
        {gap && !q.task && (
          <Button size="sm" variant="outline" className="h-8 text-[11px] px-2" onClick={raise} data-testid={`question-task-${q.key}`}>
            <ListPlus className="w-3 h-3 mr-1" /> Task</Button>
        )}
        {q.task && <span className="text-[10px] text-[#008080] font-mono" data-testid={`question-task-linked-${q.key}`}>{q.task.task_ref}</span>}
      </div>
    </div>
  );
};
